import { Link } from 'react-router-dom';
import PageTemplate from '../../../components/feature/PageTemplate';
import SharedProcessSection from '../../../components/feature/SharedProcessSection';
import SharedFAQSection from '../../../components/feature/SharedFAQSection';

function Hero() {
  return (
    <section className="relative min-h-[70vh] flex flex-col justify-end overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/e397e63bbf464e9e93c33a839f15ceb5.jpg"
          alt="Roof repair Utah"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/50 to-charcoal/20"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-16 pt-36 w-full">
        <div className="flex items-center gap-2 text-white/50 font-body text-xs mb-4">
          <Link to="/" className="hover:text-white cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-white/80">Roof Repair</span>
        </div>
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-brand-orange text-white text-xs font-700 uppercase tracking-widest px-4 py-1.5 rounded-full mb-5 font-body">
            <i className="ri-alarm-warning-fill"></i> Fast Response Available
          </div>
          <h1 className="font-heading font-900 text-white text-5xl md:text-7xl lg:text-8xl uppercase tracking-tight leading-none mb-4">
            Roof Repair Utah —<br />
            <span className="text-brand-orange">Fast &amp; Reliable</span>
          </h1>
          <p className="font-body text-white/70 text-lg mb-8 max-w-2xl leading-relaxed">
            From minor leaks to major storm damage, our certified repair crews respond quickly — protecting your home before small issues become expensive disasters.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/#estimate" className="flex items-center gap-2 bg-brand-orange hover:bg-brand-orange-dark text-white font-heading font-800 text-base uppercase tracking-widest px-8 py-3.5 rounded-md cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-calendar-check-fill"></i> Get Repair Quote
            </Link>
            <a href="tel:+18015550199" className="flex items-center gap-2 border border-white/30 hover:border-white text-white font-heading font-800 text-base uppercase tracking-widest px-8 py-3.5 rounded-md cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-phone-fill text-brand-orange"></i> (801) 555-0199
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function MainSection() {
  const REPAIRS = [
    { icon: 'ri-drop-fill', title: 'Leak Repair', desc: 'Fast identification and sealing of active leaks before water damage spreads into insulation, walls, and framing.' },
    { icon: 'ri-wind-fill', title: 'Storm & Wind Damage', desc: 'Repair of missing, lifted, or cracked shingles caused by Utah\'s high-wind events and severe weather.' },
    { icon: 'ri-flashlight-fill', title: 'Flashing Repair', desc: 'Re-sealing and replacement of damaged chimney, vent, and valley flashing — a leading cause of roof leaks.' },
    { icon: 'ri-contrast-drop-fill', title: 'Ponding & Drainage', desc: 'Fixing drainage issues on flat or low-slope roofs that cause standing water and accelerated membrane damage.' },
    { icon: 'ri-sun-fill', title: 'UV & Heat Damage', desc: 'Restoring cracked, blistered, or granule-depleted shingles caused by Utah\'s intense summer sun exposure.' },
    { icon: 'ri-snowflake-fill', title: 'Ice Dam Damage', desc: 'Safe ice dam removal and repair of resulting leaks, damaged eaves, and attic moisture intrusion.' },
  ];

  return (
    <section className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="inline-block w-16 h-1 bg-brand-orange rounded-full mb-6"></div>
          <h2 className="font-heading font-900 text-charcoal text-5xl md:text-6xl uppercase tracking-tight leading-none mb-4">
            Roof Repairs <span className="text-brand-orange">We Handle</span>
          </h2>
          <p className="font-body text-charcoal/60 text-lg max-w-xl mx-auto">Comprehensive repair solutions for every type of roofing damage — fast and lasting.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {REPAIRS.map((r) => (
            <div key={r.title} className="bg-surface hover:bg-surface-dark transition-colors rounded-2xl p-7">
              <div className="w-12 h-12 flex items-center justify-center bg-brand-orange/10 rounded-lg mb-4">
                <i className={`${r.icon} text-brand-orange text-2xl`}></i>
              </div>
              <h3 className="font-heading font-900 text-charcoal text-xl uppercase tracking-wide mb-2">{r.title}</h3>
              <p className="font-body text-charcoal/60 text-sm leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>
        {/* Emergency Banner */}
        <div className="bg-charcoal rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 flex items-center justify-center bg-brand-orange rounded-xl flex-shrink-0">
              <i className="ri-alarm-warning-fill text-white text-3xl"></i>
            </div>
            <div>
              <h3 className="font-heading font-900 text-white text-2xl uppercase tracking-wide">Emergency Roof Repair Available</h3>
              <p className="font-body text-white/50 text-sm">Active leak? Severe storm damage? Call us now for priority response across Utah.</p>
            </div>
          </div>
          <a href="tel:+18015550199" className="flex items-center gap-2 bg-brand-orange hover:bg-brand-orange-dark text-white font-heading font-800 text-base uppercase tracking-widest px-8 py-3.5 rounded-md cursor-pointer whitespace-nowrap transition-colors flex-shrink-0">
            <i className="ri-phone-fill"></i> Call Now
          </a>
        </div>
      </div>
    </section>
  );
}

function AIBlock() {
  return (
    <section className="py-28 bg-charcoal">
      <div className="max-w-5xl mx-auto px-6">
        <div className="bg-charcoal-light rounded-2xl p-10 border-l-4 border-brand-orange">
          <h2 className="font-heading font-900 text-white text-4xl md:text-5xl uppercase tracking-tight mb-6">
            Expert <span className="text-brand-orange">Roof Repair</span> in Utah
          </h2>
          <p className="font-body text-white/70 text-lg leading-relaxed mb-8">
            <strong className="text-white">Roof repair</strong> addresses specific damage to your existing roofing system — extending its useful life without the cost of full replacement. Utah&apos;s climate creates unique repair needs: heavy snow-load stress, summer UV degradation, spring hail impacts, and freeze-thaw cycling all demand fast professional attention.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'Act Fast — Here\'s Why', icon: 'ri-alarm-warning-fill', items: ['1 gallon of leak = $30K+ damage potential', 'Mold grows within 24–48 hours', 'Deck rot spreads quickly', 'Insurance may deny delayed claims', 'Small repairs prevent total replacements'] },
              { title: 'Common Utah Repairs', icon: 'ri-tools-fill', items: ['Missing or lifted shingles', 'Flashing failures at chimneys', 'Hail impact damage', 'Ice dam related leaks', 'Valley and ridge failures'] },
              { title: 'Our Repair Guarantee', icon: 'ri-shield-check-fill', items: ['Written repair scope', 'Before & after photos', 'Workmanship warranty', 'Insurance-ready docs', 'Same-day response available'] },
            ].map((col) => (
              <div key={col.title}>
                <div className="flex items-center gap-2 mb-3">
                  <i className={`${col.icon} text-brand-orange text-xl`}></i>
                  <h3 className="font-heading font-800 text-brand-orange text-sm uppercase tracking-widest">{col.title}</h3>
                </div>
                <ul className="space-y-2">
                  {col.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 font-body text-white/60 text-sm">
                      <i className="ri-arrow-right-s-fill text-brand-orange flex-shrink-0 mt-0.5"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const PROCESS = [
  { icon: 'ri-search-eye-line', title: 'Inspection', desc: 'We identify all damage sources — not just the obvious ones — to give you a complete repair scope.' },
  { icon: 'ri-file-text-line', title: 'Detailed Quote', desc: 'Itemized repair estimate with photos. No surprise charges — you approve everything before we start.' },
  { icon: 'ri-box-3-line', title: 'Material Match', desc: 'We source matching materials to maintain your roof\'s appearance and warranty coverage.' },
  { icon: 'ri-hammer-line', title: 'Expert Repair', desc: 'Certified crew performs all repairs to manufacturer specifications and Utah code requirements.' },
  { icon: 'ri-checkbox-circle-line', title: 'Quality Check', desc: 'Final inspection with photos confirms all repairs are complete and performing correctly.' },
];

const FAQS = [
  { q: 'How do I know if my roof needs repair?', a: 'Common signs include water stains on ceilings or walls, missing or curling shingles, granules accumulating in gutters, visible daylight in your attic, or a roof that is fifteen or more years old. Any active leak requires immediate professional attention to prevent escalating water damage to your home.' },
  { q: 'Can a small roof leak wait to be fixed?', a: 'Absolutely not — even minor leaks cause serious damage quickly. Water infiltration leads to mold growth within twenty-four to forty-eight hours, accelerates deck rot, damages insulation, and can void your homeowners insurance coverage if you delay repairs. Contact IWC Roofing the same day you discover any leak.' },
  { q: 'How much does roof repair cost in Utah?', a: 'Minor roof repairs in Utah typically range from $300 to $1,500 depending on the type and extent of damage. More significant repairs involving flashing, decking, or multiple areas may run $1,500 to $5,000. IWC Roofing provides free inspections and completely transparent estimates with no hidden fees.' },
  { q: 'How long does a roof repair take?', a: 'Most minor repairs are completed in two to four hours. More extensive damage involving multiple areas, flashing replacement, or deck repairs may take a full day. We always provide a realistic timeline upfront and communicate any changes immediately so you can plan accordingly.' },
  { q: 'Will my insurance cover roof repair?', a: 'Insurance typically covers repairs caused by sudden, covered events like hail, wind, or fire. Damage from neglect or normal wear is usually not covered. IWC Roofing helps document damage thoroughly and works with your insurance company to maximize your approved claim for qualifying repair work.' },
];

export default function RoofRepairPage() {
  return (
    <PageTemplate
      hero={<Hero />}
      mainSection={<MainSection />}
      aiBlock={<AIBlock />}
      processSection={<SharedProcessSection title="Our Repair Process" subtitle="Fast, thorough repairs backed by our workmanship guarantee." steps={PROCESS} />}
      faqSection={<SharedFAQSection title="Repair Questions Answered" faqs={FAQS} />}
      midCTA1="Don't let a small leak become a big problem — call Utah's repair experts."
      midCTA2="Most repairs completed same or next day — get your free quote now."
      midCTA3="Fast, reliable repairs trusted by Utah homeowners since 2010."
    />
  );
}
